/**
 * =============================================================================
 * Helper: affiliate-presence
 * Versão: 1.0.0
 *
 * Descrição:
 *   Calcula o estado de presença de um afiliado a partir de `last_seen_at`.
 *   Reaproveitado pelo painel admin (lista + drawer) para exibir badges
 *   "Online", "Ativo recentemente" e "Offline há …".
 * =============================================================================
 */

export type PresenceStatus = 'online' | 'recent' | 'away' | 'offline';

export interface PresenceState {
  status: PresenceStatus;
  /** Texto curto para badge/coluna. Ex.: "Online", "há 5 min". */
  label: string;
  /** Texto detalhado p/ tooltip. */
  detail: string;
  /** Classe Tailwind do "ponto" colorido. */
  dotClass: string;
}

const ONLINE_THRESHOLD_MS = 2 * 60 * 1000;   // 2 min  → online
const RECENT_THRESHOLD_MS = 15 * 60 * 1000;  // 15 min → ativo recentemente
const AWAY_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 h  → ausente

/** Formata distância humana curta (sem libs externas). */
function shortDistance(ms: number): string {
  if (ms < 60_000) return 'agora';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  return `há ${d} d`;
}

export function getPresenceState(
  lastSeenAt: string | null | undefined,
): PresenceState {
  if (!lastSeenAt) {
    return {
      status: 'offline',
      label: 'Sem acesso',
      detail: 'Este afiliado ainda não acessou o painel.',
      dotClass: 'bg-muted-foreground/40',
    };
  }

  const seen = new Date(lastSeenAt).getTime();
  const diff = Date.now() - seen;

  if (diff <= ONLINE_THRESHOLD_MS) {
    return {
      status: 'online',
      label: 'Online',
      detail: `Ativo agora (${shortDistance(diff)})`,
      dotClass: 'bg-emerald-500',
    };
  }

  if (diff <= RECENT_THRESHOLD_MS) {
    return {
      status: 'recent',
      label: shortDistance(diff),
      detail: `Última atividade ${shortDistance(diff)}`,
      dotClass: 'bg-amber-500',
    };
  }

  if (diff <= AWAY_THRESHOLD_MS) {
    return {
      status: 'away',
      label: shortDistance(diff),
      detail: `Última atividade ${shortDistance(diff)}`,
      dotClass: 'bg-muted-foreground/60',
    };
  }

  return {
    status: 'offline',
    label: shortDistance(diff),
    detail: `Última atividade ${shortDistance(diff)}`,
    dotClass: 'bg-muted-foreground/40',
  };
}
